import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { exhaustMap, map, switchMap, tap } from 'rxjs/operators';
import { LocalStorage } from 'src/app/shared/providers/local-storage/local-storage';
import { Storage } from '@ionic/storage';
import { CrtLrReglageCollection, CrtLrReglage } from '../../models/reglage-lr';

/**
 * Service de reglage PH
 */
@Injectable({
  providedIn: 'root',
})
export class CrtReglageLrService extends LocalStorage {
  /**
   * Nombre de reglages (nombre de lois)
   */
  public settingsSubject: Subject<number>;

  /**
   * LR mise à jour impliquant une mise à jour du stockage local
   */
  public updateTempLRSubject: Subject<number>;

  /**
   * Clef de stockage local pour les reglages des lois de régulation
   * @type {string}
   */
  private readonly ENVOIS_LR_STORAGE_KEY = 'crt-envois-${idSite}-lr';

  /**
   * Constructeur
   * @param {Storage} storage
   */
  constructor(protected override storage: Storage) {
    super();
    this.settingsSubject = new Subject();
    this.updateTempLRSubject = new Subject();
  }

  /**
   * Récupère la liste de tous les règlages de PH
   * déjà efféctués à partir du stockage local
   * @returns Observable<CrtLrReglageCollection[]>
   */
  public getAllReglagesLR(): Observable<CrtLrReglageCollection[]> {
    const baseKey = this.formatStorageKey(this.ENVOIS_LR_STORAGE_KEY, {
      idSite: '.+',
    });
    return fromPromise<string[]>(this.storage.keys()).pipe(
      map((keys) => keys || []),
      switchMap((keys) => {
        return fromPromise(this.loadAllLrReglagesByKeys(keys.filter((key) => key.match(new RegExp(baseKey)))));
      })
    );
  }

  /**
   * Récupere toutes les LR via un tableau de clés
   * @param keys
   */
  private async loadAllLrReglagesByKeys(keys: string[]) {
    const reglages: CrtLrReglageCollection[] = [];
    const baseKey = this.formatStorageKey(this.ENVOIS_LR_STORAGE_KEY, {
      idSite: '(.+)',
    });

    for (let key of keys) {
      let result: CrtLrReglageCollection = await this.storage.get(key);

      if (result) {
        const firstLR: CrtLrReglage = result[Object.keys(result)[0]];
        if (firstLR) {
          result = {
            ...result,
            idSite: parseInt(key.match(new RegExp(baseKey))[1]),
            siteDescription: firstLR.descriptionSite,
            dkCodeSite: firstLR.dkCodeSite,
          };

          reglages.push(result);
        }
      }
    }
    return reglages;
  }

  /**
   * Récupère tous les reglages de LR d'un site
   * @param idSite
   * @returns {Observable<CrtLrReglageCollection>}
   */
  public getAllLrReglagesByIdSite(idSite: number): Observable<CrtLrReglageCollection> {
    const key = this.formatStorageKey(this.ENVOIS_LR_STORAGE_KEY, {
      idSite,
    });
    return fromPromise(this.storage.get(key)).pipe(
      map((r: CrtLrReglageCollection) => r || {}),
      tap((reglages) => this.settingsSubject.next(Object.keys(reglages).length))
    );
  }

  /**
   * Recupère les réglages d'une LR
   * @param idSite
   * @param idLr
   */
  public getLrReglageByIdSiteAndLrId(idSite: number, idLr: number): Observable<CrtLrReglage> {
    return this.getAllLrReglagesByIdSite(idSite).pipe(
      map((reglages: CrtLrReglageCollection) => {
        return reglages[idLr];
      })
    );
  }

  /**
   * Suppression d'un reglage de LR
   * @param {string} idSite
   * @param {number} lrId
   * @param {number} updateStockageTemp enverra un warning pour indiquer qu'un delete a été fait
   * et qu'une mise à jour du stockage temporaire est nécessaire
   * @returns {Observable<CrtPhReglageCollection>}
   */
  public deleteLRReglageWithIdSite(idSite: number, lrId: number, updateStockageTemp: boolean) {
    return this.getAllLrReglagesByIdSite(idSite).pipe(
      exhaustMap((reglages) => {
        const newReglages = Object.keys(reglages).reduce((result, key) => {
          if (key !== '' + lrId) {
            result[key] = reglages[key];
          }
          return result;
        }, {});
        this.settingsSubject.next(Object.keys(newReglages).length);
        return this.setLrReglagesByIdSite(idSite, newReglages).pipe(
          tap(() => {
            if (updateStockageTemp) {
              this.updateTempLRSubject.next(lrId);
            }
          })
        );
      })
    );
  }

  /**
   * Insert (ou remplace) les reglage d'un loi de régulation
   * @param siteId
   * @param lrId
   * @param reglages
   */
  public insertOrReplaceLRReglageWithIdSite(
    idSite: number,
    lrId: number,
    reglages: CrtLrReglage
  ): Observable<CrtLrReglageCollection> {
    return this.getAllLrReglagesByIdSite(idSite).pipe(
      tap((reglagescollection) => {
        const newReglages = reglagescollection;
        newReglages[lrId] = reglages;
        this.settingsSubject.next(Object.keys(newReglages).length);
        return this.setLrReglagesByIdSite(idSite, newReglages);
      })
    );
  }

  /**
   * Insert des reglages LR pour un site
   * @param {number} siteId
   * @param {CrtPhReglageCollection} reglages
   * @returns {Observable<CrtPhReglageCollection>}
   */
  setLrReglagesByIdSite(idSite: number, reglages: CrtLrReglageCollection): Observable<CrtLrReglageCollection> {
    const key = this.formatStorageKey(this.ENVOIS_LR_STORAGE_KEY, {
      idSite,
    });
    return fromPromise(this.storage.set(key, reglages)).pipe(
      tap(() => this.settingsSubject.next(Object.keys(reglages).length))
    );
  }
}
